import { useCallback, useEffect, useRef, useState } from 'react';
import { getValidatedConfig } from '../../config';
import { Routes } from '../../Routes';
import { useAppContext } from '../context';
import { useApi } from '../../platform/api/useApi';
import { wait } from './util';
import { errorReasons } from '../services/mixpanelEvents';

export function useClaims() {
  const context = useAppContext();
  const apis = useApi();
  // Claims are fetched once on mount, so start out loading rather than flipping the flag from
  // inside the effect. Already-loaded claims (a remount from a later route) skip the fetch.
  const [loading, setLoading] = useState(context.claims == null);
  const hasValidatedRef = useRef(false);
  const validateClaims = useCallback(async () => {
    try {
      const [result] = await Promise.all([getValidatedConfig(context, apis), wait(1000)]);
      if (result === null) {
        context.updateClaims(null);
        context.setRoute(Routes.ERROR, errorReasons.CLAIMS_INVALID);
        return;
      } else {
        context.updateClaims(result);
        setLoading(false);
        return;
      }
    } catch {
      context.updateClaims(null);
      setLoading(false);
      context.setRoute(Routes.ERROR, errorReasons.CLAIMS_FETCH_FAILED);
    }
  }, [context, apis]);

  useEffect(() => {
    // The ref, not the loading flag, is what keeps this to one attempt: the catch path clears
    // loading, so a flag-based guard re-entered here and refetched without bound.
    if (!hasValidatedRef.current && !context.claims) {
      hasValidatedRef.current = true;
      void validateClaims();
      return;
    }

    if (
      !loading &&
      !([Routes.ERROR] as Routes[]).includes(context.currentRoute) &&
      !context.claims
    ) {
      context.setRoute(Routes.ERROR, errorReasons.CLAIMS_MISSING);
      return;
    }
    if (
      !loading &&
      !([Routes.ERROR] as Routes[]).includes(context.currentRoute) &&
      context.claims &&
      context.claims.expiryTime !== -1 &&
      context.claims.expiryTime < new Date().getTime() / 1000
    ) {
      context.setRoute(Routes.TIMEOUT);
      return;
    }

    if (!context.claims) {
      return;
    }
    // We subtract 60s because PE requires >60s left in order to allow order creation
    const expiryMs = (context.claims.expiryTime - 60) * 1000 - Date.now();
    const timeoutMs = Math.min(expiryMs, Math.pow(2, 31) - 1);
    const timeoutId = window.setTimeout(() => context.setRoute(Routes.TIMEOUT), timeoutMs);

    return () => window.clearTimeout(timeoutId);
  }, [loading, context.claims, validateClaims, context.currentRoute]);

  return loading;
}
